import React, { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Clock, MessageSquare } from 'lucide-react';
import { useSiteContent } from '../../lib/siteContent';
import { LandingSectionShell } from './LandingSectionShell';

export default function ContactForm() {
  const { content } = useSiteContent();
  const c = content.contact;
  const headingRef = useRef<HTMLHeadingElement>(null);
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: '-60px' });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('section-visible');
        }
      },
      { threshold: 0.1 }
    );

    if (headingRef.current) {
      observer.observe(headingRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <LandingSectionShell id="contact" className="!pb-28">
      <div ref={sectionRef} className="container mx-auto px-4">
        <div className="mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="mb-4 flex justify-center"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
              <MessageSquare className="h-3.5 w-3.5 text-emerald-400" />
              Contact
            </span>
          </motion.div>

          <div className="mb-14 text-center">
            <h2 ref={headingRef} className="section-heading-saas--on-dark">
              Talk to product
            </h2>
            <p className="mx-auto max-w-2xl text-slate-400">
              Procurement, security questionnaire, or a guided pilot—route it like an enterprise SaaS team. We respond
              from the same surface your users already trust.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-5 lg:gap-8">
            <motion.div
              initial={{ opacity: 0, x: -12 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.45, delay: 0.08 }}
              className="landing-glass-panel relative overflow-hidden p-8 lg:col-span-2"
            >
              <div className="pointer-events-none absolute -bottom-20 -right-20 h-48 w-48 rounded-full bg-emerald-500/10 blur-3xl" />
              <h3 className="relative mb-8 font-display text-xl font-semibold tracking-tight text-white">
                Direct lines
              </h3>

              <div className="relative space-y-6">
                {[
                  { Icon: Phone, label: 'Phone', value: c.phone, href: `tel:+917799001866` },
                  { Icon: Mail, label: 'Email', value: c.email, href: `mailto:${c.email}` },
                  { Icon: MapPin, label: 'Address', value: c.address },
                  { Icon: Clock, label: 'Hours', value: c.hours },
                ].map(({ Icon, label, value, href }) => (
                  <div key={label} className="flex items-start gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.05]">
                      <Icon className="h-5 w-5 text-emerald-400/90" />
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
                      {href && value.trim() ? (
                        <a href={href} className="mt-0.5 block font-medium text-slate-200 transition-colors hover:text-emerald-300">
                          {value}
                        </a>
                      ) : (
                        <p className="mt-0.5 font-medium text-slate-200">{value.trim() ? value : '—'}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="relative mt-10 border-t border-white/[0.06] pt-8">
                <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Follow</p>
                <div className="flex flex-wrap gap-2">
                  {['linkedin', 'twitter', 'github'].map((social) => (
                    <a
                      key={social}
                      href={`#${social}`}
                      className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-xs font-medium text-slate-300 transition-colors hover:border-emerald-500/30 hover:text-white"
                    >
                      {social}
                    </a>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 12 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.45, delay: 0.12 }}
              className="landing-glass-panel p-8 lg:col-span-3"
            >
              <form className="space-y-5" onSubmit={(e) => e.preventDefault()}>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      First name
                    </label>
                    <input
                      type="text"
                      className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-colors focus:border-emerald-500/50 focus:bg-white/[0.07] focus:ring-2 focus:ring-emerald-500/20"
                      placeholder="Jordan"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Last name
                    </label>
                    <input
                      type="text"
                      className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-colors focus:border-emerald-500/50 focus:bg-white/[0.07] focus:ring-2 focus:ring-emerald-500/20"
                      placeholder="Lee"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Work email
                    </label>
                    <input
                      type="email"
                      className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-colors focus:border-emerald-500/50 focus:bg-white/[0.07] focus:ring-2 focus:ring-emerald-500/20"
                      placeholder="you@institution.com"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Phone
                    </label>
                    <input
                      type="tel"
                      className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-colors focus:border-emerald-500/50 focus:bg-white/[0.07] focus:ring-2 focus:ring-emerald-500/20"
                      placeholder="+1 · optional"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    Topic
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-colors focus:border-emerald-500/50 focus:bg-white/[0.07] focus:ring-2 focus:ring-emerald-500/20"
                    placeholder="Security review, pilot, pricing…"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    Message
                  </label>
                  <textarea
                    className="w-full resize-none rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-colors focus:border-emerald-500/50 focus:bg-white/[0.07] focus:ring-2 focus:ring-emerald-500/20"
                    rows={4}
                    placeholder="Context, timelines, and what success looks like."
                  />
                </div>

                <button
                  type="submit"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-b from-emerald-500 to-emerald-700 px-8 py-3.5 text-sm font-semibold text-white shadow-lg shadow-emerald-950/40 transition-all hover:from-emerald-400 hover:to-emerald-600 sm:w-auto"
                >
                  <span>Send message</span>
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </LandingSectionShell>
  );
}
